import { useEffect, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateTime } from "@/lib/format";
import { APP_LABELS, METHOD_LABELS, deviceLabel, isoDaysAgo, locationLabel } from "./labels";

type UserRow = components["schemas"]["UserActivityUserRow"];
type SignInRow = components["schemas"]["UserSignInRow"];

// The longest window the API accepts (366 days inclusive).
const LOOKBACK_DAYS = 365;

/** Sign-in activity on the user detail page: last seen, last location and device, and the last 20 sign-ins. */
export function UserSignInActivity({ userId }: { userId: string }) {
  const [summary, setSummary] = useState<UserRow | null>(null);
  const [signIns, setSignIns] = useState<SignInRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const window = { From: isoDaysAgo(LOOKBACK_DAYS), To: isoDaysAgo(0) };
      const [users, recent] = await Promise.all([
        api.GET("/admin/user-activity/users", { params: { query: { ...window, UserId: userId, Limit: 1 } } }),
        api.GET("/admin/user-activity/sign-ins", { params: { query: { ...window, UserId: userId, Limit: 20 } } }),
      ]);
      if (cancelled) return;
      if (users.error || recent.error) {
        setError("Sign-in activity could not be loaded.");
      } else {
        setSummary(users.data?.data?.[0] ?? null);
        setSignIns(recent.data?.data ?? []);
      }
      setLoading(false);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>Sign-in activity</CardTitle>
        <CardDescription>
          Location is approximate (city-level, from the IP address) and only known for sign-ins through Dolfin's own apps.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Spinner className="size-5" />
          </div>
        ) : error ? (
          <p className="text-destructive text-sm">{error}</p>
        ) : (
          <>
            <dl className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4 [&>div]:min-w-0">
              <Fact label="Last sign-in" value={summary?.lastSignInAt ? formatDateTime(summary.lastSignInAt) : null} />
              <Fact label="Last active" value={summary?.lastActiveAt ? formatDateTime(summary.lastActiveAt) : null} />
              <Fact label="Last location" value={locationLabel(summary?.lastCity, summary?.lastCountry)} empty="Unknown" />
              <Fact
                label="Last device"
                value={deviceLabel(summary?.lastDeviceType, summary?.lastOs, summary?.lastBrowser)}
                empty="Unknown"
              />
            </dl>
            {signIns.length === 0 ? (
              <p className="text-muted-foreground text-sm">No sign-ins in the last year.</p>
            ) : (
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>App</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Device</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {signIns.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="whitespace-nowrap">{formatDateTime(s.occurredAt)}</TableCell>
                        <TableCell>{METHOD_LABELS[s.method]}</TableCell>
                        <TableCell>{APP_LABELS[s.app]}</TableCell>
                        <TableCell>{locationLabel(s.city, s.country) ?? <span className="text-muted-foreground">Unknown</span>}</TableCell>
                        <TableCell>{deviceLabel(s.deviceType, s.os, s.browser)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Fact({ label, value, empty = "Never" }: { label: string; value: string | null; empty?: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-sm">{label}</dt>
      <dd className="mt-1 text-sm">{value ?? <span className="text-muted-foreground">{empty}</span>}</dd>
    </div>
  );
}
